<template>
  <div>
    <v-alert v-if="error" :value="true" type="error">
      {{ error }}
    </v-alert>
    <!-- Leaflet sidebar markup -->
    <MapSidebar :items="items" :zoneTree="zoneTree" @filterUpdate="filterUpdate"
      @moveToCoords="(latitude, longitude, zoom) => $refs.map.moveToCoords(latitude, longitude, zoom)"
      @fitToBounds="() => $refs.map.fitToBounds(this.markers.getBounds())"
      @clusteringStateUpdate="updateClusteringState" />
    <!-- Instance of GenericMap to use for displaying device status data -->
    <GenericMap :centerPopups="true" ref="map" />
  </div>
</template>

<script>
import Vue from 'vue'
import GenericMap from '@/components/Common/GenericMap.vue'
import MapSidebar from './MapSidebar.vue'
import ClusterMarker from './ClusterMarker.vue'
import MarkerPopup from './MarkerPopup.vue'
import L from 'leaflet'
import 'leaflet-sidebar-v2'
import 'leaflet-sidebar-v2/css/leaflet-sidebar.css'
import 'leaflet.markercluster'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster.freezable'
import 'leaflet.featuregroup.subgroup'

export default {
  name: 'DeviceStatusMap',
  components: { GenericMap, MapSidebar },

  data () {
    return {
      error: false,
      items: [],
      baseitems: [],
      markers: undefined,
      markerLookup: undefined,
      zoneTree: undefined,
      clustered: true
    }
  },
  watch: {
    /**
     * Update icons on map when item list changes
     *
     * @param {Object[]} newItems
     */
    items (newItems) {
      if (this.markers !== undefined) {
        this.markers.remove()
      }
      if (newItems.length > 0) {
        const map = this.$refs.map.getMap()
        this.generateClusters(map, newItems)
        if (!this.clustered) {
          this.markers.disableClustering()
        }
      }
    },
    clustered (newClustered) {
      if (newClustered) {
        this.markers.enableClustering()
      } else {
        this.markers.disableClustering()
      }
    }
  },
  methods: {
    openPopupByName (name) {
      const marker = this.markerLookup[name]
      if (marker !== undefined) {
        const map = this.$refs.map.getMap()
        map.setView(marker._latlng, 17.5)
        marker.openPopup()
      }
    },
    fitToBoundsByName (names) {
      const markers = Object.entries(this.markerLookup).filter(x => names.includes(x[0]))
      const bounds = markers.map(x => x[1]._latlng)
      this.$refs.map.fitToBounds(bounds)
    },
    /**
     * Update the items list based on device type and severity filters
     *
     * @param {Object} values
     */
    filterUpdate (values) {
      // Only filter by device type if zones were fetched successfully
      const inSelectedDevicetypes = (typename) => this.zoneTree === undefined ? true : values.selectedDevicetypes.includes(typename)
      this.items = this.baseitems.filter(x => inSelectedDevicetypes(x.typename) && values.selectedSeverities.includes(x.severity))
    },
    /**
     * Get the organisations, project groups, and device types and return them in a format compatible with Vuetify treeview.
     *
     * @returns {Object | undefined} Tree view items or undefined if fetch failed
     */
    async fetchZoneTree () {
      try {
        const devicetypesResponse = await this.$http.get('/schemas/devicetypes')
        const devicetypes = devicetypesResponse.data.devicetypes.map(item => {
          return { id: `devicetype_${item.id}`, platformid: item.id, name: item.typename }
        })

        const projectgroupsResponse = await this.$http.get('/schemas/projectgroups')
        const projectgroups = projectgroupsResponse.data.projectgroups.map(item => {
          const children = devicetypes.filter(item2 => item.devicetypes.includes(item2.platformid))
          return { id: `projectgroup_${item.id}`, platformid: item.id, name: item.groupname, children }
        })

        const organisationsResponse = await this.$http.get('/schemas/organisations')
        const organisations = organisationsResponse.data.organisations.map(item => {
          const children = projectgroups.filter(item2 => item.groups.includes(item2.platformid))
          return { id: `organisation_${item.id}`, platformid: item.id, name: item.companyname, children }
        })

        return organisations
      } catch (error) {
        this.error = 'Error fetching zones'
      }
    },
    /**
     * Enables or disables marker clustering on the map
     * Gets called when user clicks 'Enable Clustering' checkbox in map settings.
     *
     * @param {Event} event The HTMLElement change event for 'Enable Clustering' checkbox
     */
    updateClusteringState (checked) {
      this.clustered = checked
    },
    errorHandler (error) {
      this.error = error
    },
    /**
     * Converts numeric severity to class names used for severity based styling
     *
     * @param {Number} severity Severity number (0-5) to be converted to the relevant class name
     *
     * @returns {string} Class name for a given severity
     */
    severityToClass (severity) {
      switch (severity) {
        case 1:
          return 'status-info'
        case 2:
          return 'status-warning'
        case 3:
          return 'status-minor'
        case 4:
          return 'status-major'
        case 5:
          return 'status-critical'
        default:
          return 'status-normal'
      }
    },
    /**
     * Generates Leaflet icon for an individual device
     *
     * @param {Number} severity Severity number (0-5) for icon colour
     *
     * @returns {divIcon}
     */
    createIcon (severity) {
      const className = this.severityToClass(severity)
      return L.divIcon({ html: `<div class="${className}"></div>`, className: 'devicestatus-marker', iconSize: [7, 7] })
    },
    /**
     * Generates Leaflet icon for clustered markers
     * The icon shows the total number of child markers in the center, with the number of child markers for each severity shown around the outside.
     *
     * @param {*} cluster An instance of a class from Leaflet.markercluster containing data about a cluster
     *
     * @returns {divIcon}
     */
    createClusterIcon (cluster) {
      // Get counts for each severity
      const markers = cluster.getAllChildMarkers()
      let totalMarkers = 0
      const severityTotals = [0, 0, 0, 0, 0, 0] // The index is the severity
      for (var i = 0; i < markers.length; i++) {
        severityTotals[markers[i].status] += 1
        totalMarkers += 1
      }

      /**
       * Generates HTMLElement for cluster icon
       * TODO: Experiment with only showing number containers with a count above 0 and dynamically shifting colours
       *
       * @param {Number} totalMarkers The total count of child markers
       * @param {Number[]} severityTotals The count for each severity in a list where the index is the severity
       *
       * @returns {HTMLElement}
       */
      const generateClusterMarkerElement = (totalMarkers, severityTotals) => {
        const ClusterMarkerClass = Vue.extend(ClusterMarker)
        const clusterMarkerInstance = new ClusterMarkerClass({
          propsData: {
            severityTotals,
            totalMarkers
          }
        })
        clusterMarkerInstance.$mount()
        return clusterMarkerInstance.$el
      }

      return L.divIcon({
        html: generateClusterMarkerElement(totalMarkers, severityTotals),
        className: 'device-status-cluster-icon'
      })
    },
    /**
     * Generates the popup that appears when a device marker is clicked
     *
     * @param {Number} severity Severity number (0-5) to colour the popup
     * @param {string} deviceName The device name (Sigfox ID) to use as the title for the popup
     * @param {string} displayName The displayname (short address) to use as the subtitle for the popup
     * @param {string} alertMessage A short description of the current alert
     *
     * @returns {*} A leaflet popup object
     */
    createMarkerPopupContent (severity, deviceName, displayName, alertMessage, typeName) {
      return L.popup({ className: `device-popup-${this.severityToClass(severity)}` }).setContent(() => {
        const MarkerPopupClass = Vue.extend(MarkerPopup)
        const markerPopupInstance = new MarkerPopupClass({
          propsData: {
            header: deviceName,
            content: displayName,
            alert: alertMessage,
            devicetype: typeName,
            $router: this.$router
          }
        })
        markerPopupInstance.$mount()
        return markerPopupInstance.$el
      })
    },
    /**
     * Generates cluster group from items and adds it to a map, also adds a reference to the cluster group to this.markers
     *
     * @param {*} map A leaflet map reference to add the clusters to
     * @param {Object[]} items A list of items to populate the markers
     */
    async generateClusters (map, items) {
      const markers = L.markerClusterGroup({
        maxClusterRadius: 150,
        iconCreateFunction: this.createClusterIcon,
        spiderfyOnMaxZoom: false,
        showCoverageOnHover: true,
        disableClusteringAtZoom: 15,
        zoomToBoundsOnClick: false // Disable default behaviour so clusterclick event works
      })

      const markerLookup = {}

      // Stop cluster icons appearing off screen and under sidebar after zoom to bounds on click
      markers.on('clusterclick', cluster => cluster.layer.zoomToBounds({ padding: [100, 100] }))

      items.forEach(item => {
        const identityData = item.identity_data
        // Stop invalid markers being created
        if (identityData.loc_lat !== undefined && identityData.loc_lon !== undefined && identityData.loc_lat !== 0 && identityData.loc_lon !== 0) {
          const icon = this.createIcon(item.severity)
          const m = L.marker([item.identity_data.loc_lat, item.identity_data.loc_lon], { title: item.device.name, icon: icon })
          m.status = item.severity
          m.typename = item.typename
          m.bindPopup(this.createMarkerPopupContent(item.severity, item.device.name, item.identity_data.displayname, item.statusdesc, item.typename))
          markerLookup[item.device.name] = m
          m.addTo(markers)
        }
      })

      markers.addTo(map)
      // Store reference to markers in data
      this.markers = markers
      this.markerLookup = markerLookup
    },
    /**
     * Initialise map features and populate it with data
     *
     * @param {*} map A leaflet map reference
     */
    async mapInit (map) {
      L.control.sidebar({
        closeButton: true, // Whether to add a close button to the panes
        container: 'sidebar', // The DOM container or #ID of a predefined sidebar container that should be used
        position: 'left' // 'left' or 'right'
      }).addTo(map)
      map.fire('dataloading') // Make loading icon appear while data is being fetched

      // Fetch statuses and devices

      this.zoneTree = await this.fetchZoneTree()

      try {
        const statusResponse = await this.$http.get('/devicestatus')
        const statuses = statusResponse.data.statuses
        const devicesResponse = await this.$http.get('/schemas/devices')
        const devices = devicesResponse.data.devices

        // Combine data from both sources
        const data = []
        statuses.forEach(item => {
          const device = devices.find(device => device.devicename === item.device.name)
          data.push({ ...item, identity_data: device.identity_data, typename: device.typename })
        })

        // Update items, triggers map update (see items watcher)
        this.baseitems = data
        this.items = data
      } catch (error) {
        this.error = 'Error populating map'
      }
      map.fire('dataload') // Hide loading icon when data has been fetched
    }
  },
  mounted () {
    // Wait until next tick so map will be initialised
    this.$nextTick(async () => {
      const map = this.$refs.map.getMap()
      this.mapInit(map)
    })
  }
}
</script>
<style src="./DeviceStatusMap.css"></style>
